import React, { useState, useEffect, useRef } from 'react';
import { uploadPhotos } from '../../utils/photoUpload';
import CameraCapture from '../CameraCapture/CameraCapture';
import { projectAPI, crewsAPI, projectContactsAPI, customerAPI } from '../../services/api';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import ProjectContactsPicker from './ProjectContactsPicker';
import { contactDisplayName } from '../../utils/contactName';
import { formatPhone, phoneForSave } from '../../utils/phone';
import { STATUS_SELECT_OPTIONS } from '../../constants/projectStatus';
import { WORK_SECTIONS } from '../../constants/workSections';
import CustomFieldInputs, { useIntakeCustomFields, useBuiltInFieldConfig } from '../CustomFields/CustomFieldInputs';
import { leadSourceValueFromIndex } from '../../constants/leadSources';
import { PROJECT_TYPES } from '../../constants/projectTypes';
import { CLASSIFICATION_SELECT_OPTIONS } from '../../constants/classifications';
import useLeadSources from '../../utils/useLeadSources';
import useZipLookup from '../../utils/useZipLookup';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';
import { SHINGLE_BRAND_NAMES, SHINGLE_PROFILES, shingleLinesFor } from '../../constants/shingles';
import './projectModal.css';

// Every field the form owns, in its "nothing filled in yet" state. One list,
// shared by the initial state, the reset-on-close path, and the seeded
// (add-from-contact / duplicate) paths, so a new field is only declared once.
const BLANK_FORM = {
    customerId: '',
    projectName: '',
    poNumber: '',
    description: '',
    type: 2, // ResidentialRetail default
    classification: 0, // ResidentialRequest default
    salesRepId: '',
    scheduledInstallerId: '',
    scheduledCrewId: '',
    status: 0, // Lead
    dateOfLoss: '',
    insuranceCarrier: '',
    claimNumber: '',
    estimatedCost: '',
    finalCost: '',
    amountPaid: '',
    estimateDate: '',
    contractSignedDate: '',
    scheduledStartDate: '',
    completionDate: '',
    materialDeliveryDate: '',
    propertyName: '',
    jobSiteStreet: '',
    jobSiteLine2: '',
    jobSiteCity: '',
    jobSiteState: 'TN',
    jobSiteZip: '',
    workOrderStartDate: '',
    shingleType: '',
    shingleBrand: '',
    shingleLine: '',
    shingleColor: '',
    sidingColor: '',
    hasMetalWork: false,
    metalWorkDescription: '',
    gutters: false,
    skylights: false,
    gutterGuards: false,
    gutterRepair: false,
    siding: false,
    windowScreens: false,
    windowTrim: false,
    standingSeam: false,
    chaseCover: false,
    interior: false,
    priorityCustomer: false,
    flatRoof: 0,
    signatureStatus: 0,
    invoiceStatus: 0,
    invoiceItem: '',
    invoiceItemOther: '',
    invoiceDueDate: '',
    repairPrice: '',
    repairLabor: '',
    gutterColor: '',
    gutterFeet: '',
    downspoutFeet: '',
    oneStoryDownspoutCount: '',
    twoStoryDownspoutCount: '',
    materialPickupMARS: false,
    materialPickupSRS: false,
    materialPickupABC: false,
    // Property Management
    tenantCustomerId: '',
    tenantName: '',
    tenantContact: '',
    // Work order scope (for additional-work templates)
    workOrderType: '',
    workOrderText: '',
    // Repair / inspection notes
    repairWorkOrderText: '',
    repairDescription: '',
    repairCompletionNotes: '',
    notRoofRelatedNotes: '',
    overviewReportNotes: '',
    // JN parity fields
    leadSource: '',
    currentInstaller: '',
    changeOrder: false,
    isNotRoofRelated: false,
    notes: ''
};

// The date inputs want yyyy-MM-dd; the API sends full ISO timestamps.
const toDateInput = (v) => (v ? new Date(v).toISOString().split('T')[0] : '');

// An API project row → form state. Used both when editing a project and when
// seeding a new one from an existing project ("Duplicate"), so the two can't
// drift apart as fields are added. Anything the caller didn't supply falls back
// to its blank value, which is what makes a partial seed like { customerId }
// work for "Add Project" off a contact.
const projectToForm = (p) => ({
    ...BLANK_FORM,
    customerId: p.customerId || '',
    projectName: p.projectName || '',
    poNumber: p.poNumber || '',
    description: p.description || '',
    type: p.type ?? BLANK_FORM.type,
    classification: p.classification ?? BLANK_FORM.classification,
    propertyName: p.propertyName || '',
    jobSiteStreet: p.jobSiteStreet || '',
    jobSiteLine2: p.jobSiteLine2 || '',
    jobSiteCity: p.jobSiteCity || '',
    jobSiteState: p.jobSiteState || '',
    jobSiteZip: p.jobSiteZip || '',
    salesRepId: p.salesRepId || '',
    scheduledInstallerId: p.scheduledInstallerId || '',
    scheduledCrewId: p.scheduledCrewId || '',
    status: p.status ?? BLANK_FORM.status,
    dateOfLoss: toDateInput(p.dateOfLoss),
    insuranceCarrier: p.insuranceCarrier || '',
    claimNumber: p.claimNumber || '',
    estimatedCost: p.estimatedCost || '',
    finalCost: p.finalCost || '',
    amountPaid: p.amountPaid || '',
    estimateDate: toDateInput(p.estimateDate),
    contractSignedDate: toDateInput(p.contractSignedDate),
    scheduledStartDate: toDateInput(p.scheduledStartDate),
    completionDate: toDateInput(p.completionDate),
    materialDeliveryDate: toDateInput(p.materialDeliveryDate),
    workOrderStartDate: toDateInput(p.workOrderStartDate),
    shingleType: p.shingleType || '',
    shingleBrand: p.shingleBrand || '',
    shingleLine: p.shingleLine || '',
    shingleColor: p.shingleColor || '',
    sidingColor: p.sidingColor || '',
    hasMetalWork: p.hasMetalWork || false,
    metalWorkDescription: p.metalWorkDescription || '',
    gutters: p.gutters || false,
    skylights: p.skylights || false,
    gutterGuards: p.gutterGuards || false,
    gutterRepair: p.gutterRepair || false,
    siding: p.siding || false,
    windowScreens: p.windowScreens || false,
    windowTrim: p.windowTrim || false,
    standingSeam: p.standingSeam || false,
    chaseCover: p.chaseCover || false,
    interior: p.interior || false,
    priorityCustomer: p.priorityCustomer || false,
    flatRoof: p.flatRoof ?? BLANK_FORM.flatRoof,
    signatureStatus: p.signatureStatus ?? BLANK_FORM.signatureStatus,
    invoiceStatus: p.invoiceStatus ?? BLANK_FORM.invoiceStatus,
    invoiceItem: p.invoiceItem || '',
    invoiceItemOther: p.invoiceItemOther || '',
    invoiceDueDate: toDateInput(p.invoiceDueDate),
    repairPrice: p.repairPrice ?? '',
    repairLabor: p.repairLabor ?? '',
    gutterColor: p.gutterColor || '',
    gutterFeet: p.gutterFeet ?? '',
    downspoutFeet: p.downspoutFeet ?? '',
    oneStoryDownspoutCount: p.oneStoryDownspoutCount ?? '',
    twoStoryDownspoutCount: p.twoStoryDownspoutCount ?? '',
    materialPickupMARS: p.materialPickupMARS || false,
    materialPickupSRS: p.materialPickupSRS || false,
    materialPickupABC: p.materialPickupABC || false,
    tenantCustomerId: p.tenantCustomerId || '',
    tenantName: p.tenantName || '',
    tenantContact: p.tenantContact || '',
    workOrderType: p.workOrderType || '',
    workOrderText: p.workOrderText || '',
    repairWorkOrderText: p.repairWorkOrderText || '',
    repairDescription: p.repairDescription || '',
    repairCompletionNotes: p.repairCompletionNotes || '',
    notRoofRelatedNotes: p.notRoofRelatedNotes || '',
    overviewReportNotes: p.overviewReportNotes || '',
    leadSource: p.leadSource || '',
    currentInstaller: p.currentInstaller || '',
    changeOrder: p.changeOrder || false,
    isNotRoofRelated: p.isNotRoofRelated || false,
    notes: p.notes || ''
});

// `seedProject` prefills a *new* project: either a whole project being duplicated
// or a partial like { customerId } from a contact's "Add Project" button. Unlike
// `editingProject` it never becomes an update — submitting always creates.
const ProjectModal = ({ isOpen, onClose, onProjectCreated, editingProject = null, seedProject = null, customers = [], users = [] }) => {
    const [formData, setFormData] = useState(BLANK_FORM);
    const [crews, setCrews] = useState([]);
    // Per-section crewing: [{ section, assignee }] where assignee is a "crew:3" /
    // "user:7" token, same encoding as the job-level picker. Empty on most jobs —
    // one crew does everything and the job-level assignee covers it.
    const [sectionCrews, setSectionCrews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Project Name follows the chosen contact until someone writes their own — a
    // job is usually named after whose property it's on, and typing that by hand
    // every time is the most repetitive part of adding one.
    const [projectNameTouched, setProjectNameTouched] = useState(false);
    // Once the job site has been edited by hand, stop overwriting it when the
    // contact changes — otherwise picking the PM contact would wipe the building
    // address someone just typed.
    const [jobSiteTouched, setJobSiteTouched] = useState(false);
    const [leadSourceTouched, setLeadSourceTouched] = useState(false);
    // Photos picked before the project exists. Held as Files and uploaded after the
    // create call returns an id; There is nothing to attach them to until then. 
    const [stagedPhotos, setStagedPhotos] = useState([]);
    const [photoProgress, setPhotoProgress] = useState(null);
    const [photoTrouble, setPhotoTrouble] = useState(null);
    const [showCamera, setShowCamera] = useState(false);
    const [previews, setPreviews] = useState([]);
    const photoInputRef = useRef(null);
    const leadSourceOptions = useLeadSources();

    const lookupJobSiteZip = useZipLookup(React.useCallback(({ city, state }) => {
        setFormData(prev => ({
            ...prev,
            ...(city ? { jobSiteCity: city } : {}),
            ...(state ? { jobSiteState: state } : {}),
        }));
    }, []));

    // Contacts attached to the job beyond the primary. The primary itself lives in
    // formData.customerId, so nothing downstream needs to change.
    const [extraContacts, setExtraContacts] = useState([]);
    // What was on the job when it opened, so an edit can work out which rows to
    // add and which to delete rather than clearing and rewriting the lot.
    const [initialExtras, setInitialExtras] = useState([]);

    // Custom fields marked "Intake" on the admin page. Loaded rather than hardcoded,
    // which is the point of the feature: Trey adds a field and it appears here with
    // no deploy. The hook also handles loading existing values on edit and saving
    // only what changed.
    const customFields = useIntakeCustomFields('Project', isOpen, editingProject?.id);

    // Admin-set choices for built-in fields. Empty until someone configures one, so
    // every field keeps the control it has always had by default.
    const builtInConfig = useBuiltInFieldConfig('Project', isOpen);

    // Contacts added from the picker's inline form during this modal session.
    const [createdContacts, setCreatedContacts] = useState([]);

    // Create a contact without leaving the job you are entering. Splits the typed
    // name the same way the tenant auto-create does — "Jane Smith" into first and
    // last, a single word into a first name rather than guessing at a surname.
    //
    // Type is Homeowner (0), the default for a contact on a job. Anything else is a
    // deliberate classification someone should make on the Contacts page, and
    // guessing it here would quietly mis-file people.
    const handleCreateContact = async ({ name, phone, email }) => {
        const parts = name.trim().split(' ').filter(Boolean);
        const res = await customerAPI.create({
            firstName: parts[0],
            lastName: parts.length > 1 ? parts.slice(1).join(' ') : null,
            displayName: name.trim(),
            phone: phoneForSave(phone) || null,
            email: email || null,
            type: 0,
            entityKind: 0,
        });
        const created = res.data;
        setCreatedContacts((prev) => [created, ...prev]);
        return created;
    };

    const isEditing = !!editingProject;

    // PropertyManagement = 4, PropertyManagementResidential = 5 on ProjectType;
    // PropertyManagement = 3 on Classification. Any one of these marks agency work.
    //
    // Lead Source counts too, and has to: a warranty job for a PM client spends its
    // Classification on "Warranty", so Lead Source is the only field left saying the
    // property is managed. Without it here the tenant fields stay hidden, and since
    // the backend now routes those jobs to the tenant, there would be no way to enter
    // the person the text is meant for.
    //
    // Must match IsPropertyManagementJob in AutomationService, which decides who
    // actually receives the text — if these two disagree, the form asks for a tenant
    // the send path ignores, or hides one it needs.
    const isPropertyManagement =
        [4, 5].includes(parseInt(formData.type, 10)) ||
        parseInt(formData.classification, 10) === 3 ||
        (formData.leadSource || '').trim().toLowerCase() === 'property management';

    // From the shared constant, not a hand-kept copy. The copy that used to live
    // here labelled value 3 (Commercial) as "Property Management", so the list had
    // two identically-named options and picking the first one silently saved a
    // Commercial job — which then fails every automation that matches
    // type equals PropertyManagement.
    const typeOptions = PROJECT_TYPES.map(t => ({ value: t.index, label: t.label }));

    const classificationOptions = CLASSIFICATION_SELECT_OPTIONS;

    // Full status list (incl. the imported JobNimbus/Engage stages) from the shared
    // source of truth, so the enum lives in exactly one place.
    const statusOptions = STATUS_SELECT_OPTIONS;

    // The contact on the project being edited or duplicated may not be in the
    // fetched list (it's capped, and filtered by type). Without it the picker
    // falls back to its placeholder and the job looks like it lost its customer —
    // the value is still submitted, but there's no way to tell from the screen.
    const sourceCustomer = editingProject?.customer || seedProject?.customer;
    const withSource = sourceCustomer && !customers.some(c => c.id === sourceCustomer.id)
        ? [sourceCustomer, ...customers]
        : customers;

    // Contacts created from inside this modal. `customers` is a prop the parent
    // fetched, so a contact made here is absent from it until the parent refetches —
    // which happens on save, far too late for the picker to show what you just
    // added. Held locally and prepended, the same trick sourceCustomer uses above.
    const selectableCustomers = createdContacts.length
        ? [...createdContacts, ...withSource]
        : withSource;

    // Format customers for SearchableSelect. Property managers and other company
    // rows have no first/last name, so the label comes from the shared fallback
    // rather than interpolating nulls.
    const customerOptions = selectableCustomers.map(customer => {
        const name = contactDisplayName(customer);
        return {
            id: customer.id,
            displayName: customer.companyName && customer.companyName !== name
                ? `${name} (${customer.companyName})`
                : name,
            // Kept alongside the label so the contacts picker can show who this
            // actually is. Searching an agency name returns dozens of people whose
            // labels differ by a word; the email or phone is what tells them apart.
            email: customer.email,
            phone: customer.phone || customer.mobilePhone || customer.mainPhone || customer.workPhone,
            companyName: customer.companyName,
            // Property address, falling back to billing — on PM contacts the property
            // side is often blank. Two people at one agency share a company name and
            // sometimes a switchboard number; the address is what separates them.
            address: [
                customer.propertyStreet || customer.billingStreet,
                customer.propertyCity || customer.billingCity,
                [customer.propertyState || customer.billingState,
                 customer.propertyZipCode || customer.billingZipCode].filter(Boolean).join(' '),
            ].filter(Boolean).join(', '),
        };
    });

    // Declared AFTER customerOptions on purpose: it reads that list, and a const is
    // unusable until its own initialiser runs. Placed above, this threw
    // "Cannot access before initialization" — but only once a tenant was picked,
    // because the ternary short-circuits while the id is empty.
    const selectedTenant = formData.tenantCustomerId
        ? customerOptions.find(o => String(o.id) === String(formData.tenantCustomerId)) || null
        : null;

    // Format users for SearchableSelect
    const userOptions = users.map(user => ({
        id: user.id,
        displayName: `${user.username} (${user.role})`
    }));

    // One assignment list holding both kinds of assignee. Crews and installers are
    // interchangeable — a job has one or the other — so they share a single picker
    // rather than competing fields. Values are namespaced ("crew:3" / "user:7") and
    // split back into the two foreign keys on save.
    //
    // Crews come first (the normal case) and only active ones are offered; a missing
    // phone is called out, since that's the difference between a dispatched work
    // order and a silent no-op.
    const assigneeOptions = [
        ...crews
            // Retired crews aren't offered — except the one already on this job, which
            // would otherwise vanish from the picker and get cleared on the next save.
            .filter(crew => crew.isActive || String(crew.id) === String(formData.scheduledCrewId))
            .map(crew => ({
                id: `crew:${crew.id}`,
                displayName: `${crew.name}${crew.leadName ? ` — ${crew.leadName}` : ''}`
                    + (crew.isActive ? '' : ' (inactive)')
                    + (crew.leadPhone ? '' : ' · no phone')
            })),
        ...users.map(user => ({
            id: `user:${user.id}`,
            // First name where there is one, matching the drawer — the usernames are
            // not always the name anyone would look for.
            displayName: `${user.firstName || user.username}${user.phone ? '' : ' · no phone'}`
        })),
    ];

    // The two FKs collapse to one picker value; whichever is set wins.
    const assigneeValue = formData.scheduledCrewId
        ? `crew:${formData.scheduledCrewId}`
        : formData.scheduledInstallerId
            ? `user:${formData.scheduledInstallerId}`
            : '';

    // Setting either kind clears the other, so the mutual exclusion the API enforces
    // can't be violated from here.
    const handleAssigneeChange = (e) => {
        const raw = e.target.value;
        if (!raw) {
            setFormData(prev => ({ ...prev, scheduledCrewId: '', scheduledInstallerId: '' }));
            return;
        }
        const [kind, id] = String(raw).split(':');
        setFormData(prev => ({
            ...prev,
            scheduledCrewId: kind === 'crew' ? id : '',
            scheduledInstallerId: kind === 'user' ? id : '',
        }));
    };

    // --- ADDING PHOTOS --- 
    const addPhotos = (files) => {
        // Appends rather than replaces: picking twice, or picking and then shooting one
        // with the camera, should add up instead of discarding the earlier batch
        setStagedPhotos(prev => [...prev, ...files]);
        // Clearing the input lets the same file be picked again after a remove; the change
        // event does not fire for an unchanged value.
        if (photoInputRef.current) photoInputRef.current.value = '';
    };

    const removePhoto = (index) => {
        setStagedPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const handleCameraCapture = (file) => {
        setShowCamera(false);
        addPhotos([file]);
    };

    // --- per-section crewing ---
    // Only sections not already listed can be added, so the "one assignee per
    // section" rule the API enforces can't be tripped from here.
    const usedSections = sectionCrews.map(s => s.section);
    const availableSections = WORK_SECTIONS.filter(s => !usedSections.includes(s.key));

    const addSectionCrew = () => {
        const next = availableSections[0];
        if (!next) return;
        setSectionCrews(prev => [...prev, { section: next.key, assignee: '' }]);
    };
    const updateSectionCrew = (idx, field, value) =>
        setSectionCrews(prev => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
    const removeSectionCrew = (idx) =>
        setSectionCrews(prev => prev.filter((_, i) => i !== idx));

    // Split the "crew:3" tokens back into the two id fields the API expects, and
    // drop rows with nobody picked — an empty section means "falls back to the
    // job-level assignee", which is the absence of a row.
    const sectionCrewPayload = () => sectionCrews
        .filter(s => s.assignee)
        .map(s => {
            const [kind, id] = String(s.assignee).split(':');
            return {
                section: s.section,
                crewId: kind === 'crew' ? parseInt(id) : null,
                installerUserId: kind === 'user' ? parseInt(id) : null,
            };
        });

    // One object URL per staged file, not one per render. This form re-renders on every 
    // keystroke, and each createObjectURL pins the whole file in memory until it is revoked.
    useEffect(() => {
        const urls = stagedPhotos.map(f => URL.createObjectURL(f));
        setPreviews(urls);
        return () => urls.forEach(URL.revokeObjectURL);
    }, [stagedPhotos]);

    // Crews are fetched here rather than passed in, since this is the only place
    // that assigns one. All of them, not just active — a job already assigned to a
    // retired crew still has to render that assignment. The picker filters.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        crewsAPI.getAll(false)
            .then(res => { if (!cancelled) setCrews(res.data || []); })
            .catch(err => console.error('Error fetching crews:', err));
        return () => { cancelled = true; };
    }, [isOpen]);

    // Section crewing lives on its own endpoint, so it loads separately from the
    // project itself. A duplicate copies the source project's split too, since a
    // repeat of the same job is normally crewed the same way. Projects started
    // from scratch (or from a contact) have none.
    const sourceProjectId = editingProject?.id || seedProject?.id;
    useEffect(() => {
        if (!isOpen) return;
        if (!sourceProjectId) {
            setSectionCrews([]);
        setExtraContacts([]);
        setInitialExtras([]);
            return;
        }
        let cancelled = false;
        projectAPI.getCrewAssignments(sourceProjectId)
            .then(res => {
                if (cancelled) return;
                setSectionCrews((res.data || []).map(a => ({
                    section: a.section,
                    assignee: a.crewId ? `crew:${a.crewId}` : a.installerUserId ? `user:${a.installerUserId}` : '',
                })));
            })
            .catch(err => console.error('Error fetching section crews:', err));
        return () => { cancelled = true; };
    }, [isOpen, sourceProjectId]);

    useEffect(() => {
        if (!isOpen) return;
        const source = editingProject || seedProject;
        if (editingProject) {
            setFormData(projectToForm(editingProject));
        } else if (seedProject) {
            // PO numbers are unique in the database, so a duplicate can't carry the
            // original's — it would collide on save. Nulling it lets Postgres accept
            // the row (the unique index allows repeated NULLs) and leaves the number
            // to be assigned to the new job.
            const seeded = projectToForm(seedProject);
            setFormData(seedProject.id ? { ...seeded, poNumber: '' } : seeded);
        } else {
            setFormData(BLANK_FORM);
        }
        // A project that already has a name keeps it — auto-fill is only for the
        // blank one you're about to type.
        setProjectNameTouched(!!(source?.projectName || '').trim());

        // Same protection for a job site that is already saved, and it is the one
        // that was missing. jobSiteTouched only ever tracked typing inside this
        // modal, so a stored address looked exactly like a prefill: opening Edit
        // Project on a PM job with no linked tenant ran the clear-on-switch effect
        // and emptied the address before anything had been touched. Saving then
        // persisted the blank, and the property manager's next email fell back to
        // the agency's own address — which is how it was noticed.
        //
        // Mirrors the fields that effect actually tests, so the two agree on what
        // counts as an address being present.
        setJobSiteTouched(
            ['jobSiteStreet', 'jobSiteCity', 'jobSiteZip']
                .some(k => String(source?.[k] ?? '').trim() !== '')
        );
    }, [isOpen, editingProject, seedProject]);

    // Name the job after the contact it's for, and keep it in step while the
    // picker is still being changed. Runs off formData rather than the picker's
    // onChange so a seeded customer (the contact page's "Add Project") fills in
    // the same way a picked one does.
    useEffect(() => {
        if (!isOpen || projectNameTouched || !formData.customerId) return;
        const picked = selectableCustomers.find(c => String(c.id) === String(formData.customerId));
        if (!picked) return;
        setFormData(prev => ({ ...prev, projectName: contactDisplayName(picked) }));
    }, [isOpen, projectNameTouched, formData.customerId]);

    // Seed the job site from the contact's property address, but only while the
    // user hasn't typed one. For most jobs the contact's address IS the job site,
    // so this keeps the common case zero-effort.
    //
    // NOT on property-management work. There the contact is the managing agency and
    // its address is an office, not the building being worked on — prefilling it
    // produces an address that looks filled in and is wrong, which is worse than a
    // blank field. It varies per job and has to be typed every time.
    useEffect(() => {
        if (!isOpen || jobSiteTouched || !formData.customerId) return;
        if (isPropertyManagement) return;
        const picked = selectableCustomers.find(c => String(c.id) === String(formData.customerId));
        if (!picked) return;
        setFormData(prev => ({
            ...prev,
            jobSiteStreet: picked.propertyStreet || '',
            jobSiteLine2: picked.addressLine2 || '',
            jobSiteCity: picked.propertyCity || '',
            jobSiteState: picked.propertyState || 'TN',
            jobSiteZip: picked.propertyZipCode || '',
        }));
        // This city and state came from the contact, not from someone typing, so the
        // ZIP lookup is free to replace them when the address is changed to the real
        // job site. Otherwise the prefill silently disables the lookup for good.
        lookupJobSiteZip.claim(picked.propertyCity, picked.propertyState || 'TN');
        // selectableCustomers is rebuilt every render; keying on the id instead
        // keeps this from looping.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, jobSiteTouched, formData.customerId]);

    // Default the lead source from the contact that the job is for. It was
    // previously typed by hand on every project, and four automations match the
    // stored string exactly — so a typo was silently a rule that never fires.
    useEffect(() => {
        if (!isOpen || leadSourceTouched || !formData.customerId) return;
        const picked = selectableCustomers.find(c => String(c.id) === String(formData.customerId));
        if (!picked) return;
        const fromContact = leadSourceValueFromIndex(picked.leadSource);
        if (!fromContact) return;
        setFormData(prev => ({ ...prev, leadSource: fromContact }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, leadSourceTouched, formData.customerId]);

    useEffect(() => {
        if (!isOpen || !editingProject?.id) { setExtraContacts([]); setInitialExtras([]); return; }
        let cancelled = false;
        projectContactsAPI.list(editingProject.id)
            .then(res => {
                if (cancelled) return;
                // Only linked contacts participate here; ad-hoc name/phone rows are
                // managed on the job's Contacts tab and are left alone.
                const rows = (res.data || [])
                    .filter(r => r.customerId)
                    .map(r => ({ id: r.id, customerId: String(r.customerId), role: r.role || '' }));
                setExtraContacts(rows);
                setInitialExtras(rows);
            })
            .catch(() => { /* contacts are additive — a failure must not block editing the job */ });
        return () => { cancelled = true; };
    }, [isOpen, editingProject]);

    // Switching a job to property management after the address was auto-filled
    // leaves the agency's office sitting in the job site. Clear it, but only if it
    // came from the prefill — anything typed by hand is left alone.
    //
    // Skipped once a tenant is chosen: their address is the job site, and the effect
    // below has already put it there. Clearing would wipe the right answer.
    useEffect(() => {
        if (!isOpen || !isPropertyManagement || jobSiteTouched) return;
        if (formData.tenantCustomerId) return;
        setFormData(prev => {
            if (!prev.jobSiteStreet && !prev.jobSiteCity && !prev.jobSiteZip) return prev;
            return { ...prev, jobSiteStreet: '', jobSiteLine2: '', jobSiteCity: '', jobSiteZip: '' };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isPropertyManagement, jobSiteTouched, formData.tenantCustomerId]);

    // On property-management work the TENANT is the one living at the property, so
    // their address is the job site — unlike the contact, who is the managing agency
    // sitting in an office somewhere else entirely.
    //
    // This is why the job site is blank rather than prefilled on a PM job: it was
    // waiting for the right person, not for typing. Still respects jobSiteTouched, so
    // an address entered by hand is never replaced, and re-runs when the tenant
    // changes so correcting the tenant corrects the address.
    useEffect(() => {
        if (!isOpen || !isPropertyManagement || jobSiteTouched) return;
        if (!formData.tenantCustomerId) return;

        const tenant = selectableCustomers.find(
            c => String(c.id) === String(formData.tenantCustomerId));
        if (!tenant) return;

        // A tenant with no address on file tells us nothing; leaving the field blank
        // and required is better than filling it with empties.
        if (!tenant.propertyStreet && !tenant.propertyCity && !tenant.propertyZipCode) return;

        setFormData(prev => ({
            ...prev,
            jobSiteStreet: tenant.propertyStreet || '',
            jobSiteLine2: tenant.addressLine2 || '',
            jobSiteCity: tenant.propertyCity || '',
            jobSiteState: tenant.propertyState || 'TN',
            jobSiteZip: tenant.propertyZipCode || '',
        }));
        // Same as the contact prefill: written by the form, so the ZIP lookup may
        // correct it if the address is edited afterwards.
        lookupJobSiteZip.claim(tenant.propertyCity, tenant.propertyState || 'TN');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isPropertyManagement, jobSiteTouched, formData.tenantCustomerId]);

    // Self-performing staff do their own work, so naming them as sales rep should
    // not then require naming them again as installer. Only fills an EMPTY installer
    // — never overwrites a choice — and only for users flagged for it, because the
    // installer is who gets the work-order text and guessing wrong sends job details
    // to someone who is not doing the job.
    useEffect(() => {
        if (!isOpen || !formData.salesRepId) return;
        if (formData.scheduledInstallerId || formData.scheduledCrewId) return;

        const rep = users.find(u => String(u.id) === String(formData.salesRepId));
        if (!rep?.autoAssignAsInstaller) return;

        setFormData(prev => ({ ...prev, scheduledInstallerId: rep.id, scheduledCrewId: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, formData.salesRepId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // A PM job without a site address is the one that cannot be worked: the
        // agency address is on file and the building's is not, so nothing downstream
        // can tell a crew where to go. Checked here rather than server-side because
        // it is a data-entry rule for this form, not an invariant of the model —
        // imported rows legitimately have no job site.
        if (isPropertyManagement && !formData.jobSiteStreet.trim()) {
            setError('Property management jobs need a job site address — the agency address is not where the work is.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const intOrNull = (v) => {
                const s = String(v ?? '').trim();
                if (s === '') return null;
                const n = parseInt(s, 10);
                return Number.isNaN(n) ? null : n;
            };
            const trimOrNull = (v) => {
                const s = String(v ?? '').trim();
                return s === '' ? null : s;
            };

            const projectData = {
                customerId: parseInt(formData.customerId),
                projectName: formData.projectName,
                poNumber: formData.poNumber.trim() || null,
                description: formData.description,
                type: parseInt(formData.type),
                classification: parseInt(formData.classification),
                propertyName: trimOrNull(formData.propertyName),
                jobSiteStreet: trimOrNull(formData.jobSiteStreet),
                jobSiteLine2: trimOrNull(formData.jobSiteLine2),
                jobSiteCity: trimOrNull(formData.jobSiteCity),
                jobSiteState: trimOrNull(formData.jobSiteState),
                jobSiteZip: trimOrNull(formData.jobSiteZip),
                salesRepId: formData.salesRepId ? parseInt(formData.salesRepId) : null,
                scheduledInstallerId: formData.scheduledInstallerId ? parseInt(formData.scheduledInstallerId) : null,
                scheduledCrewId: formData.scheduledCrewId ? parseInt(formData.scheduledCrewId) : null,
                status: parseInt(formData.status),
                dateOfLoss: formData.dateOfLoss ? new Date(formData.dateOfLoss).toISOString() : null,
                insuranceCarrier: trimOrNull(formData.insuranceCarrier),
                claimNumber: trimOrNull(formData.claimNumber),
                estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : 0,
                finalCost: formData.finalCost ? parseFloat(formData.finalCost) : null,
                amountPaid: formData.amountPaid ? parseFloat(formData.amountPaid) : null,
                estimateDate: formData.estimateDate ? new Date(formData.estimateDate).toISOString() : null,
                contractSignedDate: formData.contractSignedDate ? new Date(formData.contractSignedDate).toISOString() : null,
                scheduledStartDate: formData.scheduledStartDate ? new Date(formData.scheduledStartDate).toISOString() : null,
                completionDate: formData.completionDate ? new Date(formData.completionDate).toISOString() : null,
                materialDeliveryDate: formData.materialDeliveryDate ? new Date(formData.materialDeliveryDate).toISOString() : null,
                workOrderStartDate: formData.workOrderStartDate ? new Date(formData.workOrderStartDate).toISOString() : null,
                shingleType: formData.shingleType,
                shingleBrand: trimOrNull(formData.shingleBrand),
                shingleLine: trimOrNull(formData.shingleLine),
                shingleColor: formData.shingleColor,
                sidingColor: trimOrNull(formData.sidingColor),
                hasMetalWork: formData.hasMetalWork,
                metalWorkDescription: formData.metalWorkDescription,
                gutters: formData.gutters,
                skylights: formData.skylights,
                gutterGuards: formData.gutterGuards,
                gutterRepair: formData.gutterRepair,
                siding: formData.siding,
                windowScreens: formData.windowScreens,
                windowTrim: formData.windowTrim,
                standingSeam: formData.standingSeam,
                chaseCover: formData.chaseCover,
                interior: formData.interior,
                priorityCustomer: formData.priorityCustomer,
                flatRoof: parseInt(formData.flatRoof),
                signatureStatus: parseInt(formData.signatureStatus),
                invoiceStatus: parseInt(formData.invoiceStatus),
                invoiceItem: formData.invoiceItem.trim() || null,
                invoiceItemOther: formData.invoiceItemOther.trim() || null,
                invoiceDueDate: formData.invoiceDueDate ? new Date(formData.invoiceDueDate).toISOString() : null,
                // Null rather than 0 when blank: an unpriced repair and a repair that
                // costs nothing are different things, and estimatedCost above already
                // shows how 0-for-empty reads once it reaches a report.
                repairPrice: formData.repairPrice === '' || formData.repairPrice == null
                    ? null
                    : parseFloat(formData.repairPrice),
                repairLabor: formData.repairLabor === '' || formData.repairLabor == null
                    ? null
                    : parseFloat(formData.repairLabor),
                gutterColor: trimOrNull(formData.gutterColor),
                gutterFeet: intOrNull(formData.gutterFeet),
                downspoutFeet: intOrNull(formData.downspoutFeet),
                oneStoryDownspoutCount: intOrNull(formData.oneStoryDownspoutCount),
                twoStoryDownspoutCount: intOrNull(formData.twoStoryDownspoutCount),
                materialPickupMARS: formData.materialPickupMARS,
                materialPickupSRS: formData.materialPickupSRS,
                materialPickupABC: formData.materialPickupABC,
                tenantCustomerId: formData.tenantCustomerId ? parseInt(formData.tenantCustomerId, 10) : null,
                tenantName: trimOrNull(formData.tenantName),
                tenantContact: trimOrNull(formData.tenantContact),
                workOrderType: trimOrNull(formData.workOrderType),
                workOrderText: trimOrNull(formData.workOrderText),
                repairWorkOrderText: trimOrNull(formData.repairWorkOrderText),
                repairDescription: trimOrNull(formData.repairDescription),
                repairCompletionNotes: trimOrNull(formData.repairCompletionNotes),
                notRoofRelatedNotes: trimOrNull(formData.notRoofRelatedNotes),
                overviewReportNotes: trimOrNull(formData.overviewReportNotes),
                leadSource: trimOrNull(formData.leadSource),
                currentInstaller: trimOrNull(formData.currentInstaller),
                changeOrder: formData.changeOrder,
                isNotRoofRelated: formData.isNotRoofRelated,
                notes: formData.notes
            };

            // Section crewing is a separate endpoint keyed on the project id, so a new
            // project has to be created first before its sections can be saved.
            let projectId = editingProject?.id;
            if (isEditing) {
                await projectAPI.update(editingProject.id, { ...projectData, id: editingProject.id });
            } else {
                const created = await projectAPI.create(projectData);
                projectId = created.data?.id;
            }

            // Extra contacts need the project to exist, same as section crewing.
            if (projectId) {
                const wantedIds = extraContacts.map(x => String(x.customerId));
                const hadIds = initialExtras.map(x => String(x.customerId));

                for (const row of initialExtras) {
                    // Gone from the list, or promoted to primary — either way the extra
                    // row should not survive, or the contact would appear twice.
                    if (!wantedIds.includes(String(row.customerId))
                        || String(row.customerId) === String(formData.customerId)) {
                        await projectContactsAPI.remove(projectId, row.id).catch(() => {});
                    }
                }

                for (const row of extraContacts) {
                    if (String(row.customerId) === String(formData.customerId)) continue;
                    if (hadIds.includes(String(row.customerId))) continue;
                    await projectContactsAPI
                        .add(projectId, { customerId: parseInt(row.customerId, 10), role: row.role || null, receivesUpdates: true })
                        .catch(() => {});
                }
            }

            // Custom field values, after the project exists — same reason as section
            // crewing and extra contacts above.
            await customFields.save(projectId);

            const sections = sectionCrewPayload();
            if (projectId && (sections.length > 0 || isEditing)) {
                // On edit this also has to run when the list is empty — that's how
                // clearing every section is expressed.
                await projectAPI.setCrewAssignments(projectId, sections);
            }

            // Photos last, and after the modal would otherwise have closed: they are the 
            // slowest thing here by a wide margin, and unlike the fields above, they are
            // not recoverable. The only copy may be on a phone that is about to navigate
            // away. So the form stays open until they land, or until the user is told 
            // which ones did not.

            if (projectId && stagedPhotos.length > 0) {
                const result = await uploadPhotos(projectId, stagedPhotos,
                    (done, total) => setPhotoProgress({ done, total })
                );
                setPhotoProgress(null);

                if (result.failed.length > 0 || result.skipped.length > 0) {
                    // The job itself saved. Hold the modal open on a screen that says so,
                    // carrying only the files that did not make it, so a retry re-sends
                    // those and nothing else.
                    setPhotoTrouble({ projectId, failed: result.failed, skipped: result.skipped });
                    setStagedPhotos(result.failed);
                    onProjectCreated(); // the list behind should still refresh (the job is real)
                    setLoading(false);
                    return; // deliberately not closing
                }
            }

            onProjectCreated();
            onClose();
            resetForm();
        } catch (error) {
            let errorMessage = isEditing ? 'Failed to update project' : 'Failed to create project';
            
            if (error.response?.data?.errors) {
                const allErrors = Object.entries(error.response.data.errors).map(([field, messages]) => 
                    `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                );
                errorMessage = allErrors.join('; ');
            } else if (error.response?.data?.title) {
                errorMessage = error.response.data.title;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Retrying photos has its own path rather than re-submitting the form: by the
    // time this can be reached the project already exists, and handleSubmit has no
    // way to know that — isEditing is still false, so going back through it would
    // create a second job.
    const retryPhotos = async () => {
        setLoading(true);
        const result = await uploadPhotos(
            photoTrouble.projectId,
            photoTrouble.failed,
            (done, total) => setPhotoProgress({ done, total })
        );
        setPhotoProgress(null);
        setLoading(false);

        if (result.failed.length === 0) {
            setPhotoTrouble(null);
            onProjectCreated();
            onClose();
            resetForm();
            return;
        }

        // Only what failed again. The skipped ones were never retried — they are the
        // wrong type or over the size limit, which a retry cannot change — so they
        // stop being reported here.
        setPhotoTrouble(prev => ({ ...prev, failed: result.failed, skipped: [] }));
        setStagedPhotos(result.failed);
    };

    const resetForm = () => {
        setFormData(BLANK_FORM);
        setError('');
        // Photos live outside formData, so BLANK_FORM does not reach them and they
        // would otherwise follow the user into the next job they add.
        setStagedPhotos([]);
        setPhotoProgress(null);
        setPhotoTrouble(null);
    };

    // Editing any part of the job site stops it following the contact. Emptying
    // all of it opts back in, so an unwanted prefill is easy to undo — same
    // contract as the project-name auto-fill above.
    const handleJobSiteChange = (e) => {
        const { name, value } = e.target;
        if (name === 'jobSiteZip') {
            lookupJobSiteZip(value, formData.jobSiteCity, formData.jobSiteState);
        }
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            const anyFilled = ['jobSiteStreet', 'jobSiteLine2', 'jobSiteCity', 'jobSiteState', 'jobSiteZip']
                .some(k => String(next[k] ?? '').trim() !== '');
            setJobSiteTouched(anyFilled);
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clearing the field opts back into following the contact, so an unwanted
        // auto-fill is easy to undo.
        if (name === 'projectName') setProjectNameTouched(value.trim() !== '');
    };

    const handleClose = () => {
        onClose();
        if (!isEditing) {
            resetForm();
        }
    };

    if (!isOpen) return null;

    return (
        // No click-to-close on the backdrop: this form is long enough that a stray
        // click outside used to throw away everything typed. Closing is the X and
        // Cancel only.
        <div className="modal-overlay">
            <div className="modal-content project-modal">
                <div className="modal-header">
                    <h2 className="modal-title">
                        {isEditing
                            ? 'Edit Project'
                            : seedProject?.id
                                ? 'Duplicate Project'
                                : 'Add New Project'}
                    </h2>
                    <button className="modal-close" onClick={handleClose}>×</button>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="project-form">
                    {/* Basic Information */}
                    <div className="form-section">
                        <h3 className="section-title">Project Information</h3>
                        
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Contacts *</label>
                                <ProjectContactsPicker
                                    options={customerOptions}
                                    primaryId={formData.customerId}
                                    extras={extraContacts}
                                    onChangePrimary={(id) =>
                                        handleChange({ target: { name: 'customerId', value: id } })}
                                    onChangeExtras={setExtraContacts}
                                    onCreateContact={handleCreateContact}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Sales Rep</label>
                                <select
                                    name="salesRepId"
                                    value={formData.salesRepId}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    <option value="">— None —</option>
                                    {userOptions.map(u => (
                                        <option key={u.id} value={u.id}>{u.displayName}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Installer</label>
                                <select
                                    name="assignee"
                                    value={assigneeValue}
                                    onChange={handleAssigneeChange}
                                    className="form-input"
                                >
                                    <option value="">— Unassigned —</option>
                                    {assigneeOptions.map(o => (
                                        <option key={o.id} value={o.id}>{o.displayName}</option>
                                    ))}
                                </select>
                                <small className="form-hint">
                                    Work-order texts go to whoever is assigned here, and photos they text
                                    back file to this job.
                                </small>
                            </div>
                        </div>


                        {/* Per-section crewing. Optional: most jobs are one crew start to
                            finish, and a section left off this list is covered by the
                            assignee above. */}
                        <div className="section-crews">
                            <div className="section-crews-head">
                                <span className="section-crews-title">Different crews by section</span>
                                <button
                                    type="button"
                                    onClick={addSectionCrew}
                                    disabled={availableSections.length === 0}
                                >
                                    + Add Section
                                </button>
                            </div>

                            {sectionCrews.length === 0 ? (
                                <p className="section-crews-empty">
                                    Not split — the crew above does the whole job. Add a section to give
                                    the roof and the gutters different crews.
                                </p>
                            ) : (
                                sectionCrews.map((row, idx) => (
                                    <div key={idx} className="section-crew-row">
                                        <select
                                            value={row.section}
                                            onChange={(e) => updateSectionCrew(idx, 'section', e.target.value)}
                                        >
                                            {WORK_SECTIONS
                                                .filter(s => s.key === row.section || !usedSections.includes(s.key))
                                                .map(s => (
                                                    <option key={s.key} value={s.key}>{s.label}</option>
                                                ))}
                                        </select>
                                        <select
                                            value={row.assignee}
                                            onChange={(e) => updateSectionCrew(idx, 'assignee', e.target.value)}
                                        >
                                            <option value="">— falls back to the crew above —</option>
                                            {assigneeOptions.map(o => (
                                                <option key={o.id} value={o.id}>{o.displayName}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="section-crew-remove"
                                            onClick={() => removeSectionCrew(idx)}
                                            aria-label="Remove section"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Project Name</label>
                                <input
                                    type="text"
                                    name="projectName"
                                    value={formData.projectName}
                                    onChange={handleChange}
                                    required
                                    className="form-input"
                                    placeholder="e.g., Smith Residence Roof Replacement"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">PO #</label>
                                <input
                                    type="text"
                                    name="poNumber"
                                    value={formData.poNumber}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., CR25-0001 (optional)"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Project Type</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    {typeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Classification and Lead Source sit with Project Type because
                                the three together are what the job IS, and what every
                                automation matches on. They used to be separated by the
                                whole address block, so setting a type meant scrolling past
                                six address fields to set the two things that go with it. */}
                            <div className="form-group">
                                <label className="form-label">Classification</label>
                                <select
                                    name="classification"
                                    value={formData.classification}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    {classificationOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Lead Source</label>
                                <select
                                    name="leadSource"
                                    value={formData.leadSource}
                                    onChange={(e) => { setLeadSourceTouched(true); handleChange(e); }}
                                    className="form-input"
                                >
                                    <option value="">— None —</option>
                                    {leadSourceOptions.map(src => (
                                        <option key={src.value} value={src.value}>{src.label}</option>
                                    ))}
                                    {/* An imported project may hold a value that predates this
                                        list. Show it rather than silently rewriting the record
                                        the moment someone opens the form. */}
                                    {formData.leadSource && !leadSourceOptions.some(o => o.value === formData.leadSource) && (
                                        <option value={formData.leadSource}>{formData.leadSource} (existing)</option>
                                    )}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row checkbox-row">
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="changeOrder"
                                        checked={formData.changeOrder}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Change Order Pending
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isNotRoofRelated"
                                        checked={formData.isNotRoofRelated}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Not Roof Related (NRR)
                                </label>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Project details and scope"
                                    rows="3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Job Site — everything about WHERE the work happens, in one place.
                        Property, street address, and the tenant who lives there were
                        previously three separate places in the form, with the tenant above
                        the assignment fields and the address buried in the same row as
                        Project Type.

                        The tenant comes first because picking one fills the address from
                        their record: the order matches what actually happens. */}
                    <div className="form-section">
                        <h3 className="section-title">Job Site</h3>
                        <p className="pm-hint">
                            Where the work is. On property-management jobs the contact above
                            is the managing agency, whose office is somewhere else entirely —
                            the tenant is who lives at the property, and job texts go to them.
                        </p>

                        {selectedTenant ? (
                            // Read the picked tenant back as a card rather than leaving a
                            // name sitting in a box. This is who gets texted, so seeing
                            // that you chose the right person matters more than the input.
                            <div className="pm-tenant-card">
                                <div className="pm-tenant-head">
                                    <span className="pm-tenant-label">Tenant</span>
                                    <button
                                        type="button"
                                        className="pm-tenant-clear"
                                        onClick={() => handleChange({ target: { name: 'tenantCustomerId', value: '' } })}
                                    >
                                        Change
                                    </button>
                                </div>
                                <div className="pm-tenant-name">{selectedTenant.displayName}</div>
                                <div className="pm-tenant-detail">
                                    {[formatPhone(selectedTenant.phone), selectedTenant.email, selectedTenant.address]
                                        .filter(Boolean).join(' · ') || 'No contact details on file'}
                                </div>
                                {!selectedTenant.phone && (
                                    <div className="pm-tenant-warn">
                                        No phone on this contact — job texts cannot reach them.
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Tenant</label>
                                        <SearchableSelect
                                            options={customerOptions}
                                            value={formData.tenantCustomerId}
                                            onChange={handleChange}
                                            placeholder="Search for the tenant..."
                                            displayKey="displayName"
                                            valueKey="id"
                                            name="tenantCustomerId"
                                            detailKeys={['companyName', 'phone', 'email', 'address']}
                                        />
                                    </div>
                                </div>

                                {/* Only offered when no tenant is linked — these are
                                    alternatives, and showing both invites two answers to
                                    one question. */}
                                <div className="form-row pm-fallback">
                                    <div className="form-group">
                                        <label className="form-label">…or just a name</label>
                                        <input
                                            type="text"
                                            name="tenantName"
                                            value={formData.tenantName}
                                            onChange={handleChange}
                                            className="form-input"
                                            maxLength={200}
                                            placeholder="If they are not a saved contact"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">…and how to reach them</label>
                                        <input
                                            type="text"
                                            name="tenantContact"
                                            value={formData.tenantContact}
                                            onChange={handleChange}
                                            className="form-input"
                                            maxLength={200}
                                            placeholder="Phone or email"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Property before street: on managed work the job is referred to
                            by the complex, and the street address is the detail under it. */}
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Property Name</label>
                                <input
                                    type="text"
                                    name="propertyName"
                                    value={formData.propertyName}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={200}
                                    placeholder="Summer Glen, Hamilton Crossings, Ashley Green Condos…"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">
                                    Street Address{isPropertyManagement ? " *" : ""}
                                </label>
                                <AddressAutocomplete
                                    value={formData.jobSiteStreet}
                                    onChange={(v) => handleJobSiteChange({ target: { name: 'jobSiteStreet', value: v } })}
                                    onSelect={(a) => {
                                        // Taking a suggestion counts as editing the job
                                        // site, so it must stop the contact prefill too.
                                        setJobSiteTouched(true);
                                        setFormData(prev => ({
                                            ...prev,
                                            jobSiteStreet: a.street || prev.jobSiteStreet,
                                            jobSiteCity: a.city || prev.jobSiteCity,
                                            jobSiteState: a.state || prev.jobSiteState,
                                            jobSiteZip: a.zip || prev.jobSiteZip,
                                        }));
                                    }}
                                />
                                {isPropertyManagement && (
                                    <div className="pm-jobsite-note">
                                        {formData.tenantCustomerId
                                            ? 'Filled from the tenant — edit if the work is elsewhere on the property.'
                                            : 'Required — the contact is the managing agency. Pick a tenant and this fills from their address.'}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Suite / Unit</label>
                                <input
                                    type="text"
                                    name="jobSiteLine2"
                                    value={formData.jobSiteLine2}
                                    onChange={handleJobSiteChange}
                                    className="form-input"
                                    placeholder="Optional"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">City</label>
                                <input
                                    type="text"
                                    name="jobSiteCity"
                                    value={formData.jobSiteCity}
                                    onChange={handleJobSiteChange}
                                    className="form-input"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">State</label>
                                <input
                                    type="text"
                                    name="jobSiteState"
                                    value={formData.jobSiteState}
                                    onChange={handleJobSiteChange}
                                    className="form-input"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">ZIP</label>
                                <input
                                    type="text"
                                    name="jobSiteZip"
                                    value={formData.jobSiteZip}
                                    onChange={handleJobSiteChange}
                                    className="form-input"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Insurance Information */}

                    <div className="form-section">
                        <h3 className="section-title">Insurance Information</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Date of Loss</label>
                                <input
                                    type="date"
                                    name="dateOfLoss"
                                    value={formData.dateOfLoss}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Insurance Carrier</label>
                                <input
                                    type="text"
                                    name="insuranceCarrier"
                                    value={formData.insuranceCarrier}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., State Farm, Allstate"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Claim #</label>
                                <input
                                    type="text"
                                    name="claimNumber"
                                    value={formData.claimNumber}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={50}
                                    placeholder="Carrier's claim number"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Financial Information */}
                    <div className="form-section financial-section">
                        <h3 className="section-title">Financial Information</h3>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Estimated Cost</label>
                                <input
                                    type="number"
                                    name="estimatedCost"
                                    value={formData.estimatedCost}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    className="form-input"
                                    placeholder="0.00"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">Final Cost</label>
                                <input
                                    type="number"
                                    name="finalCost"
                                    value={formData.finalCost}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    className="form-input"
                                    placeholder="0.00"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">Amount Paid</label>
                                <input
                                    type="number"
                                    name="amountPaid"
                                    value={formData.amountPaid}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    className="form-input"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Timeline Information */}
                    <div className="form-section">
                        <h3 className="section-title">Timeline</h3>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Estimate Date</label>
                                <input
                                    type="date"
                                    name="estimateDate"
                                    value={formData.estimateDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">Contract Signed</label>
                                <input
                                    type="date"
                                    name="contractSignedDate"
                                    value={formData.contractSignedDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Scheduled Start</label>
                                <input
                                    type="date"
                                    name="scheduledStartDate"
                                    value={formData.scheduledStartDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Completion Date</label>
                                <input
                                    type="date"
                                    name="completionDate"
                                    value={formData.completionDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Material Delivery</label>
                                <input
                                    type="date"
                                    name="materialDeliveryDate"
                                    value={formData.materialDeliveryDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Invoice Due Date</label>
                                <input
                                    type="date"
                                    name="invoiceDueDate"
                                    value={formData.invoiceDueDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Work Details */}
                    <div className="form-section">
                        <h3 className="section-title">Work Details</h3>
                        
                        {/* Brand → Shingle Type (product line) → Profile → Color.
                            Brand and type are datalists rather than selects, so a
                            product we haven't catalogued can still be typed. */}
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Brand</label>
                                <input
                                    type="text"
                                    name="shingleBrand"
                                    list="shingle-brand-options"
                                    value={formData.shingleBrand}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., Owens Corning"
                                    autoComplete="off"
                                />
                                <datalist id="shingle-brand-options">
                                    {SHINGLE_BRAND_NAMES.map(b => <option key={b} value={b} />)}
                                </datalist>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Shingle Type</label>
                                <input
                                    type="text"
                                    name="shingleLine"
                                    list="shingle-line-options"
                                    value={formData.shingleLine}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., Duration, Landmark Pro"
                                    autoComplete="off"
                                />
                                {/* Narrows to the chosen brand; falls back to every
                                    line when no brand is set yet. */}
                                <datalist id="shingle-line-options">
                                    {shingleLinesFor(formData.shingleBrand).map(l => <option key={l} value={l} />)}
                                </datalist>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Profile</label>
                                <select
                                    name="shingleType"
                                    value={formData.shingleType}
                                    onChange={handleChange}
                                    className="form-input"
                                >
                                    <option value="">Select profile</option>
                                    {SHINGLE_PROFILES.map(type => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Shingle Color</label>
                                <input
                                    type="text"
                                    name="shingleColor"
                                    value={formData.shingleColor}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., Charcoal Black"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Siding Color</label>
                                <input
                                    type="text"
                                    name="sidingColor"
                                    value={formData.sidingColor}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="e.g., Sage Green"
                                />
                            </div>
                        </div>

                        <div className="form-row checkbox-row">
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="hasMetalWork"
                                        checked={formData.hasMetalWork}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Has Metal Work
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="gutters"
                                        checked={formData.gutters}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Gutters
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="skylights"
                                        checked={formData.skylights}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Skylights
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="gutterGuards"
                                        checked={formData.gutterGuards}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    Gutter Guards
                                </label>
                            </div>
                            {[
                                ['gutterRepair', 'Gutter Repair'],
                                ['siding', 'Siding'],
                                ['windowScreens', 'Window Screens'],
                                ['windowTrim', 'Window Trim'],
                                ['standingSeam', 'Standing Seam'],
                                ['chaseCover', 'Chase Cover'],
                                ['interior', 'Interior'],
                                ['priorityCustomer', 'Priority Customer'],
                            ].map(([name, label]) => (
                                <div className="form-group" key={name}>
                                    <label className="form-checkbox-label">
                                        <input
                                            type="checkbox"
                                            name={name}
                                            checked={formData[name]}
                                            onChange={handleChange}
                                            className="form-checkbox"
                                        />
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Flat Roof</label>
                                <select name="flatRoof" value={formData.flatRoof} onChange={handleChange} className="form-select">
                                    <option value={0}>None</option>
                                    <option value={1}>EPDM</option>
                                    <option value={2}>TPO</option>
                                    <option value={3}>ModBit</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Signature Status</label>
                                <select name="signatureStatus" value={formData.signatureStatus} onChange={handleChange} className="form-select">
                                    <option value={0}>None</option>
                                    <option value={1}>Sent</option>
                                    <option value={2}>Partially Signed</option>
                                    <option value={3}>Fully Signed</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Invoice Status</label>
                                <select name="invoiceStatus" value={formData.invoiceStatus} onChange={handleChange} className="form-select">
                                    <option value={0}>None</option>
                                    <option value={1}>Open</option>
                                    <option value={2}>Closed</option>
                                </select>
                            </div>
                            {/* What is being invoiced. A dropdown once an admin has given
                                Invoice Item a list of choices on the Custom Fields page,
                                a free-text box until then — the column is text either
                                way, so switching between the two never invalidates a
                                value already stored. */}
                            <div className="form-group">
                                <label className="form-label" htmlFor="invoiceItem">
                                    {builtInConfig.InvoiceItem?.label || 'Invoice Item'}
                                </label>
                                {builtInConfig.InvoiceItem?.options?.length ? (
                                    <select
                                        id="invoiceItem"
                                        name="invoiceItem"
                                        value={formData.invoiceItem}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="">— none —</option>
                                        {builtInConfig.InvoiceItem.options.map((o) => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                        {/* A job invoiced for something since removed from
                                            the list still shows its own value, rather than
                                            appearing blank and being silently cleared on
                                            the next save. */}
                                        {formData.invoiceItem
                                            && !builtInConfig.InvoiceItem.options.includes(formData.invoiceItem) && (
                                            <option value={formData.invoiceItem}>
                                                {formData.invoiceItem} (no longer offered)
                                            </option>
                                        )}
                                    </select>
                                ) : (
                                    <input
                                        id="invoiceItem"
                                        type="text"
                                        name="invoiceItem"
                                        value={formData.invoiceItem}
                                        onChange={handleChange}
                                        className="form-input"
                                        maxLength={200}
                                        placeholder="What is being invoiced"
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="invoiceItemOther">Invoice Item — Other</label>
                                <input
                                    id="invoiceItemOther"
                                    type="text"
                                    name="invoiceItemOther"
                                    value={formData.invoiceItemOther}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={500}
                                    placeholder="Free text, if it is not a standard item"
                                />
                                {/* Says this out loud because it is surprising: filling both
                                    means the customer sees this one and never the other. */}
                                <small className="form-hint">
                                    Wins over Invoice Item in customer emails when filled.
                                </small>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Repair Price</label>
                                <input
                                    type="number"
                                    name="repairPrice"
                                    value={formData.repairPrice}
                                    onChange={handleChange}
                                    className="form-input"
                                    min="0"
                                    step="0.01"
                                    placeholder="What the customer pays"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Repair Labor</label>
                                <input
                                    type="number"
                                    name="repairLabor"
                                    value={formData.repairLabor}
                                    onChange={handleChange}
                                    className="form-input"
                                    min="0"
                                    step="0.01"
                                    placeholder="What the installer is paid"
                                />
                            </div>
                        </div>

                        {formData.hasMetalWork && (
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label className="form-label">Metal Work Description</label>
                                    <textarea
                                        name="metalWorkDescription"
                                        value={formData.metalWorkDescription}
                                        onChange={handleChange}
                                        className="form-textarea"
                                        placeholder="Describe the metal work required"
                                        rows="2"
                                    />
                                </div>
                            </div>
                        )}

                        {formData.gutters && (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Gutter Color</label>
                                        <input
                                            type="text"
                                            name="gutterColor"
                                            value={formData.gutterColor}
                                            onChange={handleChange}
                                            className="form-input"
                                            maxLength={100}
                                            placeholder="e.g., Bronze, White"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Gutter Feet</label>
                                        <input
                                            type="number"
                                            name="gutterFeet"
                                            value={formData.gutterFeet}
                                            onChange={handleChange}
                                            min="0"
                                            className="form-input"
                                            placeholder="Linear ft"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Downspout Feet</label>
                                        <input
                                            type="number"
                                            name="downspoutFeet"
                                            value={formData.downspoutFeet}
                                            onChange={handleChange}
                                            min="0"
                                            className="form-input"
                                            placeholder="Linear ft"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">1-Story Downspout Count</label>
                                        <input
                                            type="number"
                                            name="oneStoryDownspoutCount"
                                            value={formData.oneStoryDownspoutCount}
                                            onChange={handleChange}
                                            min="0"
                                            className="form-input"
                                            placeholder="# units"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">2-Story Downspout Count</label>
                                        <input
                                            type="number"
                                            name="twoStoryDownspoutCount"
                                            value={formData.twoStoryDownspoutCount}
                                            onChange={handleChange}
                                            min="0"
                                            className="form-input"
                                            placeholder="# units"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Work Order (additional scope) */}
                    <div className="form-section">
                        <h3 className="section-title">Work Order (Additional Scope)</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Work Order Type</label>
                                <input
                                    type="text"
                                    name="workOrderType"
                                    value={formData.workOrderType}
                                    onChange={handleChange}
                                    className="form-input"
                                    maxLength={100}
                                    placeholder="e.g., Gutter Install, Standing Seam"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Work Order Start Date</label>
                                <input
                                    type="date"
                                    name="workOrderStartDate"
                                    value={formData.workOrderStartDate}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Work Order Description</label>
                                <textarea
                                    name="workOrderText"
                                    value={formData.workOrderText}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Free-form scope of work"
                                    rows="3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Repair / Inspection Notes */}
                    <div className="form-section">
                        <h3 className="section-title">Repair / Inspection Notes</h3>
                        {/* First in the section because it is the one that leaves the
                            building: everything else here is read inside Artemis, this
                            is texted to whoever is sent to the property. */}
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Work Order (texted to the installer)</label>
                                <textarea
                                    name="repairWorkOrderText"
                                    value={formData.repairWorkOrderText}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="What the installer is being sent to do — goes out with the dispatch text at Jobs In Progress"
                                    rows="3"
                                />
                                <span className="form-hint">
                                    Separate from Work Order (Additional Scope) above, which covers gutter and metal work.
                                </span>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Repair Description (homeowner-reported issue)</label>
                                <textarea
                                    name="repairDescription"
                                    value={formData.repairDescription}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Brief the tech on the issue before they visit"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Repair Completion Notes (what was done)</label>
                                <textarea
                                    name="repairCompletionNotes"
                                    value={formData.repairCompletionNotes}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Captured after the repair is wrapped up"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Not Roof Related Notes</label>
                                <textarea
                                    name="notRoofRelatedNotes"
                                    value={formData.notRoofRelatedNotes}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Findings when the issue isn't roof-related"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label className="form-label">Overview Report Notes</label>
                                <textarea
                                    name="overviewReportNotes"
                                    value={formData.overviewReportNotes}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Inspection findings when the roof is sound and no claim is needed"
                                    rows="2"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Material Pickup */}
                    <div className="form-section">
                        <h3 className="section-title">Material Pickup</h3>
                        
                        <div className="form-row checkbox-row">
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="materialPickupMARS"
                                        checked={formData.materialPickupMARS}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    MARS
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="materialPickupSRS"
                                        checked={formData.materialPickupSRS}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    SRS
                                </label>
                            </div>
                            <div className="form-group">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="materialPickupABC"
                                        checked={formData.materialPickupABC}
                                        onChange={handleChange}
                                        className="form-checkbox"
                                    />
                                    ABC
                                </label>
                            </div>
                        </div>
                    </div>

                    <CustomFieldInputs {...customFields} />

                    {/* Additional Notes */}
                    <div className="form-section">
                        <h3 className="section-title">Additional Notes</h3>
                        
                        <div className="form-row">
                            <div className="form-group full-width">
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Any additional notes about this project"
                                    rows="3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Photos - only when creating. On an existing job the photos tab owns this,
                        and it can upload immediately because the project already exists */}
                    {!isEditing && (
                        <div className="form-section">
                            <h3 className="section-title">Photos</h3>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <input 
                                        type="file"
                                        ref={photoInputRef}
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => addPhotos(Array.from(e.target.files))}
                                        style={{ display: 'none' }} 
                                    />
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button type="button" className="btn-secondary" onClick={() => photoInputRef.current?.click()}>
                                            <i className="fas fa-images"></i> Choose Photos                                        
                                        </button>
                                        <button type="button" className="btn-secondary" onClick={() => setShowCamera(true)}>
                                            <i className="fas fa-camera"></i> Take Photo
                                        </button>
                                    </div>

                                    {stagedPhotos.length > 0 && (
                                        <>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                                                {previews.map((url, i) => (
                                                    <div key={url} style={{ position: 'relative' }}>
                                                        <img src={url} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-color, #d1d5db)' }} />
                                                        <button type="button" onClick={() => removePhoto(i)} aria-label={`Remove ${stagedPhotos[i]?.name || 'photo'}`} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: '12px', lineHeight: 1, cursor: 'pointer' }}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                            <span className="form-hint">
                                                {stagedPhotos.length} photo(s) will upload after the job is created.
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showCamera && (
                        <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
                    )}

                    {/* Once the job has saved but its photos have not, both normal actions
                        are wrong: Cancel reads as though the job can still be abandoned,
                        and Add Project would create a second one. */}
                    {photoTrouble ? (
                        <div
                            className="modal-actions"
                            style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}
                        >
                            <div className="error-message">
                                {/* Leads with the job being saved, on purpose. The job is in the
                                    database; hearing that a photo failed must not read as having
                                    lost the work that was just typed in. */}
                                Job saved.
                                {photoTrouble.failed.length > 0
                                    && ` ${photoTrouble.failed.length} photo(s) did not upload.`}
                                {photoTrouble.skipped.length > 0
                                    && ` ${photoTrouble.skipped.length} skipped: ${photoTrouble.skipped
                                        .map(s => `${s.name} (${s.reason})`).join(', ')}.`}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                {photoTrouble.failed.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={retryPhotos}
                                        disabled={loading}
                                        className="btn-primary"
                                    >
                                        {loading
                                            ? (photoProgress
                                                ? `Retrying ${photoProgress.done}/${photoProgress.total}...`
                                                : 'Retrying...')
                                            : `Retry ${photoTrouble.failed.length} photo(s)`
                                        }
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => { setPhotoTrouble(null); onClose(); resetForm(); }}
                                    disabled={loading}
                                    className="btn-cancel"
                                >
                                    {/* Named for what it does. "Cancel" here would suggest the job
                                        goes away with it, and it does not. */}
                                    Close without them
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="modal-actions">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="btn-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary"
                            >
                                {/* Photo progress rather than a bare "Creating..." once the job is
                                    saved and the uploads are the slow part: a button that sits
                                    silent for a minute on a truck's connection gets clicked again. */}
                                {loading
                                    ? (photoProgress
                                        ? `Uploading photos ${photoProgress.done}/${photoProgress.total}...`
                                        : (isEditing ? 'Updating...' : 'Creating...'))
                                    : (isEditing ? 'Update Project' : 'Add Project')
                                }
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ProjectModal;